import React, { useReducer, useCallback, Reducer } from "react";

import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import async from "async";

import { readFile } from "../../../utilities/readFile";
import useAxios from "../../../services/useAxios";

import { uploadStateHandler, uploadStateInitialize } from "./uplaodState";

import "./upload-page.scss";
import type {
  ApiResponseType,
  AddProductExcelRequestType,
  ReturningDataFromExcelType,
  ReducerState,
  ReducerAction,
} from "./UpTypes";

const UploadPage: React.FC = () => {
  const { execute: addProductExcelAPI } = useAxios(
    "superadminapi/addproductexcel"
  );

  const [state, dispatch] = useReducer<Reducer<ReducerState, ReducerAction>>(
    uploadStateHandler,
    uploadStateInitialize
  );

  const queue = async.queue<AddProductExcelRequestType>((task, callback) => {
    addProductExcelAPI(task).then((res: ApiResponseType) => {
      if (+res.status !== 1) {
        console.log("Can't connect to server!!");
        dispatch({ type: "AddError", param: task });
        callback();
        return;
      }
      dispatch({ type: "CompleteRecord" });
      callback();
    });
  }, 20);

  queue.drain(() => {
    Swal.fire({ title: "آیتم(ها) با موفقیت اضافه شدند.", icon: "success" });
  });

  const sendApi = useCallback(
    (data: ReturningDataFromExcelType) => {
      dispatch({
        type: "AddRecord",
        param: data.length,
      });

      let qd = data.map((value) => {
        let img: string =
          value["تصویر 1"] && value["تصویر 1"] !== ""
            ? value["تصویر 1"]
            : value["تصویر 2"] && value["تصویر 2"] !== ""
            ? value["تصویر 2"]
            : value["تصویر 3"] && value["تصویر 3"]
            ? value["تصویر 3"]
            : "";
        let d: AddProductExcelRequestType = {
          title: value["نام محصول"] ?? "",
          default_price: value["قیمت"] ?? "0",
          default_weight_from: value["وزن از"] ?? "0",
          default_weight_to: value["وزن تا"] ?? "0",
          cat_id: value["کد دسته بندی"] ?? "0",
          line_id: value["کد لاین اصلی"] ?? "0",
          sub_line_id: value["کد سابلاین"] ?? "0",
          img_name: img,
          id:value["کد شناسه"] ??"0",
        };
        return d;
      });
      queue.push(qd);
    },
    [queue]
  );

  const convertName = useCallback((name: string) => {
    switch (name) {
      case "title":
        return "نام محصول";
      case "default_price":
        return "قیمت";
      case "default_weight_from":
        return "وزن از";
      case "default_weight_to":
        return "وزن تا";
      case "cat_id":
        return "کد دسته‌بندی";
      case "line_id":
        return "کد لاین اصلی";
      case "sub_line_id":
        return "کد سابلاین";
      case "img_name":
        return "تصویر";
      case "id":
        return "کد شناسه";
      default:
        return "";
    }
  }, []);

  const fileOnChangeHandler: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      async (event) => {
        let files = event.target.files ?? [];
        dispatch({
          type: "Reset",
        });
        for (let i = 0; i < files.length; i++) {
          let file = await readFile(files);
          let data = XLSX.read(file, {
            type: "binary",
            raw: true,
          });
          for (let sheetName of data.SheetNames) {
            let res: ReturningDataFromExcelType = XLSX.utils.sheet_to_json(
              data.Sheets[sheetName]
            );
            console.log(res);
            sendApi(res);
          }
        }
      },
      [sendApi]
    );

  return (
    <div className="upload-page pd-5 d-flex flex-column align-items-center justify-content-center">
      <div className="d-flex flex-column align-items-center">
        <label
          className="btn "
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          لطفا فایل مورد نظر را انتخاب کنید.
          <input
            type="file"
            className="d-none"
            onChange={fileOnChangeHandler}
            multiple
          />
        </label>
        <input
          id="completed-files-range"
          type="range"
          min={0}
          max={state.recordsCount}
          step={1}
          className="range-input mt-3"
          style={{
            backgroundSize: `${
              100 - (state.completedRecordsCount * 100) / state.recordsCount
            }% 100%`,
          }}
          disabled
        />
        <label htmlFor="completed-files-range" className="mt-4 text-center">
          تعداد رکورد های آپلود شده از تب کنونی {state.completedRecordsCount}/
          {state.recordsCount}
        </label>
      </div>

      <div className="mt-3">
        {state.errorRecords.length !== 0 && (
          <table>
            <thead>
              <tr>
                {Object.keys(state.errorRecords[0]).map((key, index) => (
                  <th key={`header-${index}`}>{convertName(key)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.errorRecords.map((value, index) => (
                <tr key={`row-${index}`}>
                  {Object.keys(value).map((key, ind) => (
                    <td key={`data-${index}-${ind}`}>
                      {value[key as keyof AddProductExcelRequestType]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UploadPage;
